import React, { useEffect } from "react";
import { Zap, AlertCircle, CheckCircle2, Camera, X, Wrench } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

/**
 * BatteryTestPanel — feature-parity battery health capture panel.
 *
 * Originally lived inline inside the standard VI item form in App.js and
 * was duplicated as a stripped-down "battery gateway" card on the
 * Diagnostic VI. Tech feedback was that the diag flow needed identical
 * capture fidelity — voltage AND measured/rated CCA AND health %, plus
 * the replacement-info block when the result is amber/red — so the two
 * surfaces ask the customer for the same data and the FOH gets the same
 * pricing hand-off.
 *
 *   Props
 *     value           — flat object holding all battery fields
 *     onChange(patch) — partial updater; called with {field: newValue}
 *     gateway         — when true, render the "GATEWAY · LOCKED" badge +
 *                       red surrounding ring when fields are incomplete
 *                       (Diagnostic VI use). Standard VI omits this.
 *     gatewayMet      — bool, only used when gateway=true
 *     locked          — when true, hides the "(Required)" suffix from the
 *                       header — used by the standard VI which gates by
 *                       traffic-light status instead.
 *     status          — "blue" | "green" | "amber" | "red" | undefined.
 *                       Drives the replacement-info block visibility.
 *     onPhotoCapture(file) — optional override for camera capture. When
 *                       not supplied, the component reads the file as a
 *                       data-URL and patches ``evidence_photo``.
 */
export default function BatteryTestPanel({
  value = {},
  onChange = () => {},
  gateway = false,
  gatewayMet = false,
  status = "amber",
  onPhotoCapture = null,
  testIdPrefix = "battery",
}) {
  const v = value || {};
  const voltage = v.battery_voltage_v ?? v.voltage ?? "";
  const measuredCca = v.measured_cca_a ?? v.measured_cca ?? "";
  const ratedCca = v.rated_cca_a ?? v.rated_cca ?? "";
  const healthPercent = v.health_percent ?? "";
  const healthPercentManual = !!v.health_percent_manual;
  const interpretation = v.interpretation || "";
  const evidencePhoto = v.evidence_photo || "";
  const batteryTypeRequired = v.battery_type_required || "";
  const batteryRegistrationRequired = v.battery_registration_required || "";
  const batteryTechnicianNote = v.battery_technician_note || "";

  // Auto-derive health % from CCA inputs when both are present and the
  // user hasn't manually overridden the field.
  useEffect(() => {
    if (healthPercentManual) return;
    const m = parseFloat(measuredCca);
    const r = parseFloat(ratedCca);
    if (!Number.isFinite(m) || !Number.isFinite(r) || r <= 0) return;
    const pct = +((m / r) * 100).toFixed(1);
    if (String(pct) !== String(healthPercent)) {
      onChange({ health_percent: pct });
    }
  }, [measuredCca, ratedCca, healthPercentManual, healthPercent, onChange]);

  const showReplacementBlock = status === "amber" || status === "red" || interpretation === "fail" || interpretation === "marginal";

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (onPhotoCapture) {
      onPhotoCapture(file);
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => onChange({ evidence_photo: reader.result });
    reader.readAsDataURL(file);
  };

  const tid = (suffix) => `${testIdPrefix}-${suffix}`;

  const wrapperClasses = gateway
    ? `p-4 rounded-lg border-2 ${
        gatewayMet
          ? "bg-emerald-50 border-emerald-400"
          : "bg-red-50 border-red-400 ring-2 ring-red-200"
      }`
    : "mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-300";

  return (
    <div className={wrapperClasses} data-testid={tid("panel")}>
      <h4 className={`font-bold mb-4 flex items-center gap-2 ${gateway ? (gatewayMet ? "text-emerald-800" : "text-red-800") : "text-yellow-800"}`}>
        {gateway ? <Zap className={`w-5 h-5 ${gatewayMet ? "text-emerald-600" : "text-red-600"}`} /> : <span>🔋</span>}
        Battery Test Results
        {gateway && (
          <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-red-600 text-white" data-testid={tid("gateway-locked-badge")}>
            GATEWAY · LOCKED
          </span>
        )}
        {gateway && gatewayMet && (
          <CheckCircle2 className="w-5 h-5 text-emerald-600 ml-auto" data-testid={tid("gateway-met")} />
        )}
      </h4>

      {gateway && !gatewayMet && (
        <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded text-sm text-red-800 flex items-start gap-2" data-testid={tid("gateway-banner")}>
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Complete voltage, interpretation and tester photo to unlock further testing.</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* Voltage */}
        <div>
          <Label className="text-sm font-semibold text-yellow-700 mb-1 block">
            Voltage (V) <span className="text-red-500">*</span>
          </Label>
          <Input
            type="number"
            step="0.1"
            min="0"
            max="20"
            value={voltage}
            onChange={(e) => onChange({ battery_voltage_v: e.target.value, reading: e.target.value })}
            placeholder="e.g. 12.4"
            className="bg-white"
            data-testid={tid("voltage-input")}
          />
          {voltage && (
            <p className={`text-xs mt-1 ${
              parseFloat(voltage) >= 12.6 ? "text-green-600" :
              parseFloat(voltage) >= 12.4 ? "text-green-500" :
              parseFloat(voltage) >= 12.2 ? "text-amber-600" :
              "text-red-600"
            }`}>
              {parseFloat(voltage) >= 12.6 ? "✓ Excellent" :
               parseFloat(voltage) >= 12.4 ? "✓ Good" :
               parseFloat(voltage) >= 12.2 ? "⚠ Fair" :
               parseFloat(voltage) >= 12.0 ? "⚠ Low" :
               "⛔ Critical"}
            </p>
          )}
        </div>

        {/* Measured CCA */}
        <div>
          <Label className="text-sm font-semibold text-yellow-700 mb-1 block">
            Measured CCA (A) <span className="text-red-500">*</span>
          </Label>
          <Input
            type="number"
            min="0"
            max="2000"
            value={measuredCca}
            onChange={(e) => onChange({ measured_cca_a: e.target.value })}
            placeholder="From tester"
            className="bg-white"
            data-testid={tid("measured-cca-input")}
          />
          <p className="text-xs text-gray-500 mt-1">From battery tester</p>
        </div>

        {/* Rated CCA */}
        <div>
          <Label className="text-sm font-semibold text-yellow-700 mb-1 block">
            Rated CCA (A) <span className="text-red-500">*</span>
          </Label>
          <Input
            type="number"
            min="0"
            max="2000"
            value={ratedCca}
            onChange={(e) => onChange({ rated_cca_a: e.target.value })}
            placeholder="From battery label"
            className="bg-white"
            data-testid={tid("rated-cca-input")}
          />
          <p className="text-xs text-gray-500 mt-1">From battery label</p>
        </div>

        {/* Health % */}
        <div>
          <Label className="text-sm font-semibold text-yellow-700 mb-1 block">
            Health %
            <span className="text-xs font-normal text-gray-500 ml-2">
              {healthPercentManual ? "(Manual)" : "(Auto-calculated)"}
            </span>
          </Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              step="0.1"
              min="0"
              max="200"
              value={healthPercent}
              onChange={(e) => onChange({ health_percent: e.target.value, health_percent_manual: true })}
              placeholder={measuredCca && ratedCca ? "Auto-calc" : "Enter %"}
              className="bg-white"
              disabled={!healthPercentManual && measuredCca && ratedCca}
              data-testid={tid("health-percent-input")}
            />
            {!healthPercentManual && measuredCca && ratedCca && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange({ health_percent_manual: true })}
                className="text-xs text-yellow-700 hover:bg-yellow-100"
                data-testid={tid("health-percent-edit")}
              >
                Edit
              </Button>
            )}
          </div>
          {healthPercent !== "" && healthPercent !== null && healthPercent !== undefined && (
            <p className={`text-xs mt-1 ${
              parseFloat(healthPercent) >= 80 ? "text-green-600" :
              parseFloat(healthPercent) >= 60 ? "text-amber-600" :
              "text-red-600"
            }`}>
              {parseFloat(healthPercent) >= 80 ? "✓ Good health" :
               parseFloat(healthPercent) >= 60 ? "⚠ Fair - monitor" :
               "⛔ Poor - replacement recommended"}
            </p>
          )}
        </div>
      </div>

      {/* Interpretation buttons */}
      <div className="mb-3">
        <Label className="text-sm font-semibold text-yellow-700 mb-2 block">
          Interpretation <span className="text-red-500">*</span>
        </Label>
        <div className="flex gap-1.5">
          {[
            { id: "pass",     label: "Pass",     colorOn: "bg-emerald-600 text-white border-emerald-700", colorOff: "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50" },
            { id: "marginal", label: "Marginal", colorOn: "bg-amber-600 text-white border-amber-700",     colorOff: "bg-white text-amber-700 border-amber-300 hover:bg-amber-50" },
            { id: "fail",     label: "Fail",     colorOn: "bg-red-600 text-white border-red-700",         colorOff: "bg-white text-red-700 border-red-300 hover:bg-red-50" },
          ].map(opt => {
            const active = interpretation === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => onChange({ interpretation: opt.id })}
                className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded border transition-colors ${active ? opt.colorOn : opt.colorOff}`}
                data-testid={tid(`interp-${opt.id}`)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Replacement info — only when amber/red OR marginal/fail */}
      {showReplacementBlock && (
        <div className="mt-4 p-3 bg-orange-50 rounded-lg border border-orange-200" data-testid={tid("replacement-block")}>
          <h5 className="font-semibold text-orange-800 mb-3 flex items-center gap-2">
            <Wrench className="w-4 h-4" />
            Battery Replacement Info (for FOH)
          </h5>

          {/* Battery Type Required */}
          <div className="mb-3">
            <Label className="text-sm font-medium text-orange-700 mb-2 block">Battery Type Required</Label>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "standard", label: "Standard Lead-Acid" },
                { value: "efb",      label: "EFB (Enhanced Flooded)" },
                { value: "agm",      label: "AGM (Absorbent Glass Mat)" },
                { value: "unknown",  label: "Unknown / Check Spec" },
              ].map(option => (
                <Button
                  key={option.value}
                  type="button"
                  variant="outline"
                  size="sm"
                  className={`text-xs ${
                    batteryTypeRequired === option.value
                      ? "bg-orange-200 border-orange-400 text-orange-800"
                      : "hover:bg-orange-100"
                  }`}
                  onClick={() => onChange({ battery_type_required: option.value })}
                  data-testid={tid(`type-${option.value}`)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
            <p className="text-xs text-orange-600 mt-1">Check battery label or vehicle spec for type</p>
          </div>

          {/* Registration Required */}
          <div className="mb-3">
            <Label className="text-sm font-medium text-orange-700 mb-2 block">Registration Required?</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={`text-xs ${
                  batteryRegistrationRequired === "yes"
                    ? "bg-red-200 border-red-400 text-red-800"
                    : "hover:bg-gray-100"
                }`}
                onClick={() => onChange({ battery_registration_required: "yes" })}
                data-testid={tid("reg-yes")}
              >
                Yes - Diagnostic Required
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={`text-xs ${
                  batteryRegistrationRequired === "no"
                    ? "bg-green-200 border-green-400 text-green-800"
                    : "hover:bg-gray-100"
                }`}
                onClick={() => onChange({ battery_registration_required: "no" })}
                data-testid={tid("reg-no")}
              >
                No - Simple Swap
              </Button>
            </div>
            <p className="text-xs text-orange-600 mt-1">
              Most modern vehicles (2012+) require battery registration via diagnostic tool
            </p>
          </div>

          {/* Tech Notes */}
          <div>
            <Label className="text-sm font-medium text-orange-700 mb-1 block">Tech Notes for FOH (optional)</Label>
            <Input
              type="text"
              value={batteryTechnicianNote}
              onChange={(e) => onChange({ battery_technician_note: e.target.value })}
              placeholder="e.g. Located in boot, requires trim removal..."
              className="bg-white text-sm"
              data-testid={tid("tech-note")}
            />
          </div>

          {batteryTypeRequired && batteryTypeRequired !== "unknown" && (
            <div className="mt-3 p-2 bg-white rounded border border-orange-200">
              <p className="text-xs text-orange-700">
                📋 This information will be displayed to FOH when pricing the battery replacement.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Tester output photo */}
      <div className="mt-3">
        <Label className="text-sm font-medium text-slate-700">Tester output photo <span className="text-red-500">*</span></Label>
        {evidencePhoto ? (
          <div className="mt-1 flex items-start gap-2" data-testid={tid("photo-preview")}>
            <img
              src={evidencePhoto}
              alt="Battery tester output"
              className="w-24 h-24 object-cover rounded border border-slate-300"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => onChange({ evidence_photo: "" })}
              data-testid={tid("photo-clear")}
            >
              <X className="w-3 h-3 mr-1" /> Re-take
            </Button>
          </div>
        ) : (
          <label
            className="mt-1 flex items-center gap-2 px-3 py-2 bg-white border-2 border-dashed border-slate-300 rounded cursor-pointer hover:bg-slate-50 w-fit"
            data-testid={tid("photo-upload")}
          >
            <Camera className="w-4 h-4 text-slate-600" />
            <span className="text-sm text-slate-700">Capture tester output</span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhotoUpload}
              className="hidden"
            />
          </label>
        )}
      </div>
    </div>
  );
}
